import { Injectable, NgZone, inject } from "@angular/core";
import {
  BehaviorSubject,
  distinctUntilChanged,
  fromEvent,
  map,
  Observable,
  Subject,
} from "rxjs";
import { IPosition } from "../models";
import {
  createPopUpWindowName,
  IPopOutHandOver,
  isPopUpWindowName,
  KEY_POPUP_HAND_OVER,
  POPUP_WINDOW_NAME
} from "../core-ui.enums";

export type FormFactor = "mobile" | "tablet" | "desktop";
@Injectable()
export class WindowService {
  private ngZone = inject(NgZone);

  public isWindowPopedOut = false;

  /**
   * a window name is shared by the whole browsing-context group, so the several
   * instances of the application a call centre can render side by side in one tab
   * (ex. a genesys interaction widget per interaction) would otherwise find - and
   * re-navigate - one another's popped-out call window. Each instance keeps a name of
   * its own, stable for as long as the page lives. It is also what marks the window it
   * names as the popped-out call, see isPopUpWindowName.
   */
  private readonly popOutWindowName = createPopUpWindowName();

  /**
   * the call window this instance opened, for as long as it is around. Kept here rather
   * than looked up by name again, see reattachPopOut.
   */
  private popOutWindow: Window | null = null;

  private _streamControlsContainerPositionChange: Subject<DOMRect> =
    new Subject();
  public streamControlsContainerPositionChange$: Observable<DOMRect> =
    this._streamControlsContainerPositionChange.asObservable();

  // body the click-outside listener is attached to. Not the main one while the call is in picture-in-picture
  private clickOutsideBody: HTMLElement;
  private onClickElements = new Set<{
    elm: HTMLElement;
    resolve: () => void;
  }>();

  private _formFactorChange = new BehaviorSubject<FormFactor>(
    this.getFormFactor()
  );
  public formFactorChange$ = this._formFactorChange.asObservable();

  constructor() {
    this.listenToResize();
  }

  private listenToResize() {
    fromEvent(window, "resize")
      .pipe(
        map(() => this.getFormFactor()),
        distinctUntilChanged()
      )
      .subscribe((formFactor) => {
        this.ngZone.run(() => this._formFactorChange.next(formFactor));
      });
  }

  /**
   * Opens the window that will host a popped out call. Call this before anything is
   * awaited: WebKit only allows window.open synchronously from the click handler, so
   * callers open the window first and navigate it later, once the conference (and with
   * it the url) is known.
   *
   * Whether the browser allows the window is answered by what it hands back, so there
   * is nothing to detect up front - asking for the window IS the check. Returns null
   * when the window was refused (blocked popup, or no gesture behind us), when it comes
   * back already closed (what some popup blockers do), or when the host has no notion
   * of multiple windows (some webviews name the current window and hand it back), so
   * that callers can fall back to opening the call in the same page.
   */
  public openPopOutPlaceholder(): Window | null {
    // the browser gives the name to whichever window it hands back - this one included,
    // where the host has no notion of a second window - so keep what we are called now.
    const previousName = this.readWindowName();

    const popup = window.open(
      "",
      this.popOutWindowName,
      "popup=true,height=600,width=900"
    );

    if (!popup || popup === window || popup.closed) {
      if (popup === window) {
        // we are the window that was just named. leaving the name behind would make
        // DeviceService treat this tab as the popped out call for the whole session.
        this.restoreWindowName(previousName);
      }
      this.popOutWindow = null;
      return null;
    }

    try {
      // the placeholder is same origin (about:blank inherits us), so we can give the
      // agent something to look at while we are creating the conference.
      popup.document.title = POPUP_WINDOW_NAME;
    } catch (ex) {
      // not fatal, the window is about to be navigated anyway
    }

    this.popOutWindow = popup;
    return popup;
  }

  /**
   * Hands back the call window this instance popped out earlier, when the component
   * that was watching it is built again. Returns null once there is no call out there
   * anymore, so the caller can stop believing there is one.
   *
   * Deliberately not a window.open by name: that opens a blank window when there is no
   * longer one to find - and an agent whose browser allows our popups, which the ones
   * signing in through a popup must, would be left looking at it while we report a call
   * in progress. This service outlives the components that watch the call, and nothing
   * remembers a popped-out call across a reload anyway, so the reference is all we need.
   */
  public reattachPopOut(): Window | null {
    try {
      if (!this.popOutWindow || this.popOutWindow.closed) {
        this.popOutWindow = null;
      }
    } catch (ex) {
      // the window is gone in a way we cannot ask about
      this.popOutWindow = null;
    }

    return this.popOutWindow;
  }

  private readWindowName(): string | null {
    try {
      return window.name;
    } catch (ex) {
      // the name is not ours to read
      return null;
    }
  }

  /**
   * Puts back the name the browser took from us when it named this window instead of
   * opening one. Embedded, that name is the host's iframe name attribute, so dropping
   * it is not ours to do either.
   */
  private restoreWindowName(previousName: string | null) {
    try {
      if (isPopUpWindowName(window.name)) {
        window.name = previousName ?? "";
      }
    } catch (ex) {
      // nothing we can do, the name is not ours to set
    }
  }

  /**
   * Navigates a window opened by openPopOutPlaceholder. A second window.open would be
   * gated on a user gesture again, navigating the window we already hold is not.
   *
   * The hand over goes to the session storage of that window: the placeholder is same
   * origin, and its storage is the one the page we navigate it to gets. It is the
   * window's own, so the other instances of a tab cannot overwrite it.
   */
  public navigatePopOut(popup: Window, url: string, handOver?: IPopOutHandOver) {
    if (handOver) {
      try {
        popup.sessionStorage.setItem(
          KEY_POPUP_HAND_OVER,
          JSON.stringify(handOver)
        );
      } catch (ex) {
        // storage is blocked, the call window makes do with what it fetches itself
      }
    }
    popup.location.replace(url);
  }

  /**
   * what the window that popped us out handed over, see navigatePopOut. null in any
   * other window.
   */
  public readPopOutHandOver(): IPopOutHandOver | null {
    try {
      return JSON.parse(sessionStorage.getItem(KEY_POPUP_HAND_OVER)) ?? null;
    } catch (ex) {
      return null;
    }
  }

  public closePopOut(popup: Window) {
    try {
      popup?.close();
    } catch (ex) {
      // the window may already be gone
    }

    if (popup === this.popOutWindow) {
      this.popOutWindow = null;
    }
  }

  /**
   * Closes the current window. Only meaningful when we are a script-opened popup
   * (see DeviceService.isPopUp). Returns false when the browser refuses to close
   * us - eg. the opener relationship was severed - so that callers can fall back
   * to normal navigation instead of leaving the user on a dead page.
   */
  public closeSelf(): boolean {
    // legacy nudge, some browsers only close windows that have an opener
    window.opener = window.self;
    window.close();
    return window.closed;
  }

  public notifyStreamControlsContainerPositionChange(position: DOMRect) {
    this._streamControlsContainerPositionChange.next(position);
  }

  private getFormFactor(): FormFactor {
    const width = window.innerWidth;
    if (width <= 599) return "mobile";
    if (width <= 959) return "tablet";
    return "desktop";
  }

  private onClickedHandler = (event: PointerEvent) => {
    let clickedOutside = 0;

    for (const entry of this.onClickElements) {
      if (!entry.elm.isConnected) {
        this.onClickElements.delete(entry);
      } else if (event.composedPath().includes(entry.elm)) {
        break;
      } else {
        clickedOutside++;
      }
    }

    if (clickedOutside === this.onClickElements.size) {
      // events of the picture-in-picture document are not patched by zone.js
      this.ngZone.run(() => {
        for (const entry of this.onClickElements) {
          entry.resolve();
        }
      });

      this.onClickElements.clear();

      this.clickOutsideBody?.removeEventListener("click", this.onClickedHandler, {
        capture: true,
      });
    }
  };

  public onClickedOutside(...elms: HTMLElement[]) {
    const targets = elms.filter((e) => !!e);

    this.clickOutsideBody?.removeEventListener("click", this.onClickedHandler, {
      capture: true,
    });

    this.clickOutsideBody = targets[0]?.ownerDocument?.body ?? document.body;
    this.clickOutsideBody.addEventListener("click", this.onClickedHandler, {
      capture: true,
    });

    return new Promise<void>((resolve) => {
      for (const elm of targets) this.onClickElements.add({ elm, resolve });
    });
  }
}
